/**
 * TeamsEditor — Sprint 33C · Feb 2026.
 *
 * Admin editor for the 4 BDC teams. Allows editing group email, CC list,
 * agent membership order (drag), and toggling active state. Round-robin
 * cursor is displayed for transparency (read-only).
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  UsersFour, EnvelopeSimple, FloppyDisk, CircleNotch, ArrowsClockwise,
  Circle, Trash, Plus,
} from "@phosphor-icons/react";

const PRESENCE_TONE = {
  available: "bg-emerald-500",
  busy: "bg-amber-500",
  away: "bg-slate-400",
  offline: "bg-slate-500",
};

// Feb 2026 — specialties available to assign to a team. Must stay in sync
// with AgentSpecialty in backend/models.py. Split in two groups for readability.
const SPECIALTY_ROLES = [
  { value: "ventas", label: "Ventas" },
  { value: "cita_taller", label: "Cita Taller" },
  { value: "servicio", label: "Servicio" },
  { value: "repuestos", label: "Repuestos" },
  { value: "test_drive", label: "Test Drive" },
  { value: "customer_care", label: "Customer Care" },
  { value: "motocicletas", label: "Motocicletas" },
];
const SPECIALTY_BRANDS = [
  { value: "cadillac", label: "Cadillac" },
  { value: "chevrolet", label: "Chevrolet" },
  { value: "gmc", label: "GMC" },
  { value: "buick", label: "Buick" },
  { value: "suzuki", label: "Suzuki" },
  { value: "nissan", label: "Nissan" },
  { value: "infiniti", label: "Infiniti" },
  { value: "yamaha", label: "Yamaha" },
];

export default function TeamsEditor({ lang }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // team_id currently saving
  const [seeding, setSeeding] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [edits, setEdits] = useState({}); // { team_id: {group_email, cc_emails_str, agent_ids} }
  const t = (es, en) => (lang === "es" ? es : en);

  const load = async () => {
    try {
      const [teamsR, usersR] = await Promise.all([
        api.get("/teams"),
        api.get("/users"),
      ]);
      setTeams(teamsR.data || []);
      setAllUsers(usersR.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    if (!window.confirm(t("¿Recrear los 4 equipos SDM y 13 agentes desde el seed?", "Recreate the 4 SDM teams and 13 agents from seed?"))) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/teams/seed");
      toast.success(t(
        `Seed OK · Equipos +${data.created_teams} · Agentes +${data.created_agents}`,
        `Seed OK · Teams +${data.created_teams} · Agents +${data.created_agents}`,
      ));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSeeding(false);
    }
  };

  const setEdit = (teamId, patch) =>
    setEdits((prev) => ({ ...prev, [teamId]: { ...(prev[teamId] || {}), ...patch } }));

  const save = async (team) => {
    const e = edits[team.id] || {};
    const upd = {};
    if ("group_email" in e) upd.group_email = e.group_email || "";
    if ("cc_emails_str" in e) upd.cc_emails = (e.cc_emails_str || "").split(",").map((s) => s.trim()).filter(Boolean);
    if ("agent_ids" in e) upd.agent_ids = e.agent_ids;
    if ("specialties" in e) upd.specialties = e.specialties;
    if ("active" in e) upd.active = e.active;
    if (Object.keys(upd).length === 0) return;
    setSaving(team.id);
    try {
      await api.patch(`/teams/${team.id}`, upd);
      toast.success(t("Equipo actualizado", "Team updated"));
      setEdits((p) => ({ ...p, [team.id]: {} }));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(null);
    }
  };

  const moveAgent = (team, idx, dir) => {
    const current = edits[team.id]?.agent_ids || team.agent_ids;
    const next = [...current];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setEdit(team.id, { agent_ids: next });
  };

  const removeAgent = (team, uid) => {
    const current = edits[team.id]?.agent_ids || team.agent_ids;
    setEdit(team.id, { agent_ids: current.filter((x) => x !== uid) });
  };

  const addAgent = (team, uid) => {
    const current = edits[team.id]?.agent_ids || team.agent_ids;
    if (current.includes(uid)) return;
    setEdit(team.id, { agent_ids: [...current, uid] });
  };

  const toggleSpecialty = (team, value) => {
    const current = edits[team.id]?.specialties || team.specialties || [];
    const next = current.includes(value)
      ? current.filter((s) => s !== value)
      : [...current, value];
    setEdit(team.id, { specialties: next });
  };

  if (loading) return <div className="text-xs text-muted-foreground flex items-center gap-1.5"><CircleNotch size={12} className="animate-spin"/>Cargando…</div>;

  return (
    <div data-testid="teams-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-muted-foreground">
          {t("Equipos con routing round-robin + notificación al correo grupal en cada asignación.",
             "Teams with round-robin routing + group-email notification on every assignment.")}
        </div>
        <Button size="sm" variant="outline" onClick={seed} disabled={seeding} data-testid="teams-seed-btn">
          {seeding ? <CircleNotch size={12} className="animate-spin mr-1"/> : <ArrowsClockwise size={12} className="mr-1"/>}
          {t("Cargar seed BDC", "Load BDC seed")}
        </Button>
      </div>

      {teams.length === 0 ? (
        <div className="text-center py-8 text-xs text-muted-foreground border border-dashed border-border rounded-md">
          {t("Sin equipos. Presiona 'Cargar seed BDC' para crear los 4 equipos y 13 agentes SDM.",
             "No teams. Click 'Load BDC seed' to create the 4 SDM teams and 13 agents.")}
        </div>
      ) : (
        <div className="space-y-3">
          {teams.map((team) => {
            const edit = edits[team.id] || {};
            const agentIds = edit.agent_ids || team.agent_ids || [];
            const groupEmail = "group_email" in edit ? edit.group_email : (team.group_email || "");
            const ccEmailsStr = "cc_emails_str" in edit ? edit.cc_emails_str : (team.cc_emails || []).join(", ");
            const dirty = Object.keys(edit).length > 0;
            const orderedAgents = agentIds
              .map((aid) => team.agents?.find((a) => a.id === aid) || allUsers.find((u) => u.id === aid))
              .filter(Boolean);
            const availableToAdd = allUsers.filter((u) => !agentIds.includes(u.id) && u.role === "bdc_agent");
            const currentSpecs = edit.specialties || team.specialties || [];

            return (
              <div
                key={team.id}
                className="border border-border rounded-md bg-card p-4"
                data-testid={`team-row-${team.code}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-heading font-bold text-sm flex items-center gap-2">
                      <UsersFour size={16} className="text-primary" />
                      {team.name}
                      <Badge variant="outline" className="text-[9px] uppercase">{team.code}</Badge>
                      {!team.active && (
                        <Badge className="text-[9px] uppercase bg-slate-500 text-white border-transparent">Inactive</Badge>
                      )}
                    </div>
                    {team.description && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">{team.description}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground">
                      RR cursor · {team.rr_cursor}
                    </span>
                    <Switch
                      checked={"active" in edit ? edit.active : team.active}
                      onCheckedChange={(v) => setEdit(team.id, { active: v })}
                      data-testid={`team-active-${team.code}`}
                    />
                  </div>
                </div>

                {/* Group email row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-[10px] uppercase text-muted-foreground block mb-1 flex items-center gap-1">
                      <EnvelopeSimple size={11}/> {t("Correo grupal", "Group email")}
                    </label>
                    <Input
                      type="email"
                      value={groupEmail}
                      onChange={(e) => setEdit(team.id, { group_email: e.target.value })}
                      placeholder="grupo@sdm.com.do"
                      className="h-8 text-xs"
                      data-testid={`team-group-email-${team.code}`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-muted-foreground block mb-1">
                      {t("CC (separados por coma)", "CC (comma separated)")}
                    </label>
                    <Input
                      type="text"
                      value={ccEmailsStr}
                      onChange={(e) => setEdit(team.id, { cc_emails_str: e.target.value })}
                      placeholder="cc1@sdm.com.do, cc2@sdm.com.do"
                      className="h-8 text-xs"
                      data-testid={`team-cc-emails-${team.code}`}
                    />
                  </div>
                </div>

                {/* Specialties (Feb 2026 remap): applied to every agent added to this team */}
                <div className="mb-3">
                  <label className="text-[10px] uppercase text-muted-foreground block mb-1.5 flex items-center gap-1">
                    {t("Especialidades del equipo", "Team specialties")}
                    <span className="text-[9px] text-muted-foreground/70 normal-case">
                      · {t("se aplican automáticamente a cada agente añadido", "auto-applied to every agent added")}
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-1">
                    {[...SPECIALTY_ROLES, ...SPECIALTY_BRANDS].map((s) => {
                      const isBrand = SPECIALTY_BRANDS.some((b) => b.value === s.value);
                      const on = currentSpecs.includes(s.value);
                      return (
                        <button
                          key={s.value}
                          onClick={() => toggleSpecialty(team, s.value)}
                          className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                            on
                              ? isBrand
                                ? "border-amber-500/60 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                                : "border-primary bg-primary/15 text-primary"
                              : "border-border hover:border-primary/40 text-muted-foreground"
                          }`}
                          data-testid={`team-spec-${team.code}-${s.value}`}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Agents list */}
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground mb-2 flex items-center justify-between">
                    <span>{t("Agentes (orden RR)", "Agents (RR order)")}</span>
                    <span>{orderedAgents.length}</span>
                  </div>
                  <div className="space-y-1">
                    {orderedAgents.map((a, idx) => (
                      <div
                        key={a.id}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-border bg-background text-xs"
                        data-testid={`team-agent-${team.code}-${idx}`}
                      >
                        <Circle size={8} weight="fill" className={PRESENCE_TONE[a.presence] || PRESENCE_TONE.offline} />
                        <span className="text-[10px] text-muted-foreground w-4">#{idx + 1}</span>
                        <span className="flex-1 truncate">
                          {a.name} <span className="text-muted-foreground">· {a.email}</span>
                        </span>
                        <button
                          onClick={() => moveAgent(team, idx, -1)}
                          disabled={idx === 0}
                          className="p-1 hover:bg-muted rounded disabled:opacity-30"
                          data-testid={`team-agent-up-${team.code}-${idx}`}
                        >↑</button>
                        <button
                          onClick={() => moveAgent(team, idx, 1)}
                          disabled={idx === orderedAgents.length - 1}
                          className="p-1 hover:bg-muted rounded disabled:opacity-30"
                          data-testid={`team-agent-down-${team.code}-${idx}`}
                        >↓</button>
                        <button
                          onClick={() => removeAgent(team, a.id)}
                          className="p-1 hover:bg-destructive/10 rounded text-destructive"
                          data-testid={`team-agent-remove-${team.code}-${idx}`}
                        >
                          <Trash size={11}/>
                        </button>
                      </div>
                    ))}
                  </div>

                  {availableToAdd.length > 0 && (
                    <div className="mt-2">
                      <select
                        onChange={(e) => e.target.value && (addAgent(team, e.target.value), (e.target.value = ""))}
                        defaultValue=""
                        className="h-8 rounded-md border border-input bg-background px-2 text-xs w-full"
                        data-testid={`team-add-agent-${team.code}`}
                      >
                        <option value="">{t("+ Añadir agente…", "+ Add agent…")}</option>
                        {availableToAdd.map((u) => (
                          <option key={u.id} value={u.id}>{u.name} · {u.email}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {dirty && (
                  <div className="flex justify-end mt-3">
                    <Button size="sm" onClick={() => save(team)} disabled={saving === team.id} data-testid={`team-save-${team.code}`}>
                      {saving === team.id ? <CircleNotch size={12} className="animate-spin mr-1"/> : <FloppyDisk size={12} className="mr-1"/>}
                      {t("Guardar cambios", "Save changes")}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
